import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, ShieldCheck, Eye, Handshake, Globe2 } from "lucide-react";
import { MaskLine } from "../motion";
import { IMG } from "../../lib/data";

const TRUST = [
  { icon: ShieldCheck, t: "Professional Consultation" },
  { icon: Eye, t: "Transparent Process" },
  { icon: Handshake, t: "End-to-End Assistance" },
  { icon: Globe2, t: "Global Opportunities" },
];

const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section id="home" ref={ref} className="relative min-h-screen overflow-hidden bg-midnight grain" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMG.hero} alt="International travel and global opportunities" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-midnight/75" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(120% 80% at 20% 20%, rgba(15,118,110,0.35), transparent 60%)" }} />
      </motion.div>

      {/* faint rotating globe ring */}
      <div className="absolute -right-40 top-1/4 h-[520px] w-[520px] rounded-full border border-white/10 spin-slow hidden md:block">
        <div className="absolute inset-8 rounded-full border border-gold/20" />
        <div className="absolute inset-20 rounded-full border border-teal/20" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 pt-40 pb-24 min-h-screen flex flex-col justify-center">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="text-xs md:text-sm uppercase tracking-[0.3em] text-gold font-bold mb-6">
          Immigration · Visas · Education · Recruitment
        </motion.p>

        <h1 className="font-serifd text-white font-light leading-[0.95] text-5xl md:text-7xl lg:text-8xl max-w-4xl">
          <MaskLine delay={0.3}>Your Gateway to</MaskLine>
          <MaskLine delay={0.45}><span className="text-gold italic">Global Opportunities</span></MaskLine>
        </h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.8 }}
          className="mt-8 text-base md:text-lg text-slate-200 max-w-xl leading-relaxed">
          Expert guidance for visas, overseas education, work permits, and international career opportunities.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-4">
          <button onClick={() => go("consultation")} data-testid="hero-consult-btn"
            className="group rounded-full bg-gold text-midnight px-8 py-4 font-semibold flex items-center gap-2 hover:bg-white transition-colors">
            Book Free Consultation <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <button onClick={() => go("destinations")} data-testid="hero-destinations-btn"
            className="rounded-full border border-white/30 text-white px-8 py-4 font-semibold hover:bg-white/10 transition-colors">
            Explore Destinations
          </button>
          <button onClick={() => go("contact")} data-testid="hero-contact-btn"
            className="rounded-full text-white/80 px-4 py-4 font-medium hover:text-gold transition-colors">
            Contact Us
          </button>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
          {TRUST.map(({ icon: Icon, t }) => (
            <div key={t} className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm px-4 py-3">
              <Icon size={20} className="text-gold shrink-0" />
              <span className="text-sm text-slate-100">{t}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
